import usePassportSchema from '@/pages/MDM/hooks/usePassprotSchema'
import { useMemo } from 'react'
import { IPassportGroup, IPassportSchema, IPassportSchemaField } from '@/pages/MDM/mdm.types'

interface IProps {
  passportSchemaId: IPassportSchema['id']
  changedPassportSchemaFieldIds: IPassportSchemaField['id'][]
}

/**
 * Возвращает список измененных IPassportGroup.
 */
const useChangedPassportGroup = (props: IProps) => {
  const passportSchema = usePassportSchema(props.passportSchemaId)

  /**
   * Возвращает измененные группы паспорта вычисляя их из
   * измененных полей схемы паспорта.
   *
   * ```
   *  groupIdByFieldId: passportSchema.fields --> [passportSchemaFieldId]: passportGroupId
   *  changedNodeGroupIds: changedPassportSchemaFieldIds[] --> groupIdByFieldId.get() --> changedPassportGroupIds
   * ```
   */
  const changedNodeGroupIds = useMemo(() => {
    if (!passportSchema) return []
    const groupIdByFieldId: Map<IPassportSchemaField['id'], IPassportSchemaField['groupId']> = new Map(
      passportSchema.fields.map((val) => [val.id, val.groupId])
    )

    const changedNodeGroupIds = new Set<IPassportSchemaField['groupId']>()
    for (let id of props.changedPassportSchemaFieldIds) {
      if (groupIdByFieldId.has(id)) changedNodeGroupIds.add(groupIdByFieldId.get(id))
    }
    return [...changedNodeGroupIds]
  }, [passportSchema, props.changedPassportSchemaFieldIds])

  /**
   * Возвращает массив группы и всех её родительских групп
   * начиная с самой группы и до корневой.
   *
   * ```
   *               1
   *              / \
   *             2   3  --> getGroupParents(5) = [5, 2, 1]
   *            / \
   *           4  [5]
   * ```
   */
  const getGroupParents = (groupId: IPassportGroup['id'], groups: IPassportGroup[]): IPassportGroup[] => {
    const groupMap: Record<number, IPassportGroup> = {}
    for (const g of groups) {
      groupMap[g.id] = g
    }

    const result: IPassportGroup[] = [groupMap[groupId]]
    let current = groupMap[groupId]

    while (current && current.parentId !== null) {
      const parent = groupMap[current.parentId]
      if (!parent) break
      result.push(parent)
      current = parent
    }

    return result
  }

  /**
   * Достает все родительские группы для каждой изменненой группы.
   * Возвращает список измененных IPassportGroup.
   */
  const changedGroups = useMemo(() => {
    return changedNodeGroupIds.map((id) => getGroupParents(id, passportSchema.groups)).flat(1)
  }, [changedNodeGroupIds, passportSchema])

  return changedGroups
}

export default useChangedPassportGroup
